import cn from 'classnames';
import { QRCodeCanvas, QRCodeSVG } from 'qrcode.react';
import { FC, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useAppSelector, useMedia } from '@/shared/hooks';
import { Button, Tooltip, WidgetCard } from '@/shared/ui';

import { composedModel } from '@/entities/composed';
import { organizationModel } from '@/entities/organization';

import {
  INLINE_QR_SIZE,
  QR_QUIET_ZONE_MODULES,
  REFERRAL_QR_PNG,
  REFERRAL_TERMS_URL,
} from '../../config';
import { useCopyReferralMessage } from '../../hooks';
import {
  downloadReferralQrPng,
  getDisplayedReferralLink,
  getReferralLink,
} from '../../lib';
import { ReferralQrModal } from '../ReferralQrModal';

import styles from './InviteColleague.module.scss';

type InviteColleagueProps = {
  className?: string;
  testID?: string;
};

const isShareCancelled = (error: unknown) =>
  error instanceof DOMException && error.name === 'AbortError';

export const InviteColleague: FC<InviteColleagueProps> = (props) => {
  const { className, testID } = props;

  const { formatMessage } = useIntl();
  const { isMobile, isPhone } = useMedia();

  const [isQrModalOpen, setIsQrModalOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const qrCanvasRef = useRef<HTMLCanvasElement>(null);

  const { isVisible, isLoading, rewards } = useAppSelector(
    composedModel.selectors.selectInviteColleagueCardState,
  );
  const organization = useAppSelector(
    organizationModel.selectors.selectCurrentOrganization,
  );

  const link = getReferralLink(organization?.ID ?? '');
  const displayedLink = getDisplayedReferralLink(link);
  const canShare = typeof navigator.share === 'function';
  const shareMessage = formatMessage(
    {
      id: 'inviteColleague.shareMessage',
      defaultMessage:
        'Hello! I recommend Diagnocat. Book a demo using my link (please use your work email): {link}. Once you are connected, a bonus awaits you: 50% off integration with your X-ray equipment or free integration with the panel on 3 devices.',
    },
    { link },
  );

  const { isCopied, copyMessage } = useCopyReferralMessage(shareMessage);

  const handleCopy = async () => {
    const isMessageCopied = await copyMessage();

    setActionError(
      isMessageCopied
        ? null
        : formatMessage({
            id: 'inviteColleague.error.copy',
            defaultMessage:
              "Couldn't copy the message. Select the link and copy it manually.",
          }),
    );
  };

  const handleShare = async () => {
    try {
      // The link is already inside the text; passing `url` too would append it a second time.
      await navigator.share({ text: shareMessage });
      setActionError(null);
    } catch (error) {
      if (isShareCancelled(error)) {
        return;
      }

      setActionError(
        formatMessage({
          id: 'inviteColleague.error.share',
          defaultMessage: "Couldn't open sharing. Copy the link instead.",
        }),
      );
    }
  };

  const showImageError = () =>
    setActionError(
      formatMessage({
        id: 'inviteColleague.error.image',
        defaultMessage: "Couldn't create the image. Try again.",
      }),
    );

  const toggleQrModal = (isOpen: boolean) => {
    setActionError(null);
    setIsQrModalOpen(isOpen);
  };

  const handleDownload = async () => {
    const qrCanvas = qrCanvasRef.current;

    if (!qrCanvas) {
      showImageError();

      return;
    }

    try {
      await downloadReferralQrPng({ qrCanvas, link });
      setActionError(null);
    } catch {
      showImageError();
    }
  };

  if (!isVisible) {
    return null;
  }

  if (isLoading) {
    return <WidgetCard loading />;
  }

  const copyButtonLabel = isCopied
    ? formatMessage({ id: 'global.copied', defaultMessage: 'Copied' })
    : formatMessage({
        id: 'inviteColleague.copyLink',
        defaultMessage: 'Copy invitation',
      });

  return (
    <WidgetCard
      className={cn(styles.container, className)}
      data-testid={testID}
      data-section="invite-colleague"
    >
      <header className={styles.header}>
        <h4 className={isPhone ? 'h5' : 'h4'}>
          {formatMessage({
            id: 'inviteColleague.title',
            defaultMessage: 'Invite a colleague',
          })}
        </h4>

        {REFERRAL_TERMS_URL && (
          <a
            className={cn(styles.link, 'p2')}
            href={REFERRAL_TERMS_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            {formatMessage({
              id: 'inviteColleague.terms',
              defaultMessage: 'Referral terms',
            })}
          </a>
        )}
      </header>

      <p className={cn(styles.secondaryText, 'p2')}>
        {rewards
          ? formatMessage(
              {
                id: 'inviteColleague.description',
                defaultMessage:
                  'Share your referral link. When a clinic you invite pays for a Diagnocat subscription, your clinic receives {referrerCoins} Diagnocoins and theirs receives a discount on integration.',
              },
              { referrerCoins: rewards.referrerCoins },
            )
          : formatMessage({
              id: 'inviteColleague.descriptionWithoutAmounts',
              defaultMessage:
                'Share your referral link. When a clinic you invite pays for a Diagnocat subscription, your clinic receives Diagnocoins and theirs receives a discount on integration.',
            })}
      </p>

      <div className={cn(styles.linkRow, isMobile && styles.linkRowMobile)}>
        <input
          dir="ltr"
          className={cn(
            styles.linkField,
            isCopied && styles.linkFieldCopied,
            'p2',
          )}
          value={displayedLink}
          readOnly
          aria-label={formatMessage({
            id: 'inviteColleague.linkLabel',
            defaultMessage: 'Your referral link',
          })}
          onFocus={(event) => event.target.select()}
        />

        {isMobile ? (
          <div className={styles.mobileActions}>
            {canShare && (
              <Button onClick={handleShare}>
                {formatMessage({
                  id: 'global.share',
                  defaultMessage: 'Share',
                })}
              </Button>
            )}

            <Button variant="secondary" onClick={handleCopy}>
              {copyButtonLabel}
            </Button>
          </div>
        ) : (
          <Button
            className={styles.copyButton}
            variant={isCopied ? 'secondary' : 'primary'}
            onClick={handleCopy}
          >
            {copyButtonLabel}
          </Button>
        )}
      </div>

      {isCopied && (
        <p className={cn(styles.copiedNote, 'p3')} role="status">
          {isMobile
            ? formatMessage({
                id: 'inviteColleague.copiedNote.mobile',
                defaultMessage:
                  'Message with the link copied — paste it into any chat or email.',
              })
            : formatMessage({
                id: 'inviteColleague.copiedNote',
                defaultMessage:
                  'Message with the link copied — paste it into WhatsApp, Telegram or an email.',
              })}
        </p>
      )}

      {actionError && !isQrModalOpen && (
        <p className={cn(styles.error, 'p3')} role="alert">
          {actionError}
        </p>
      )}

      {isMobile ? (
        <button
          type="button"
          className={cn(styles.textButton, styles.showQrButton, 'p2')}
          onClick={() => toggleQrModal(true)}
        >
          {formatMessage({
            id: 'inviteColleague.showQrCode',
            defaultMessage: 'Show QR code',
          })}
        </button>
      ) : (
        <div className={styles.qrRow}>
          <Tooltip.Primary
            content={formatMessage({
              id: 'inviteColleague.clickToEnlarge',
              defaultMessage: 'Click to enlarge',
            })}
          >
            <button
              type="button"
              className={styles.qrButton}
              aria-label={formatMessage({
                id: 'inviteColleague.enlargeQrCode',
                defaultMessage: 'Enlarge QR code',
              })}
              onClick={() => toggleQrModal(true)}
            >
              <QRCodeSVG
                value={link}
                size={INLINE_QR_SIZE}
                level="M"
                marginSize={QR_QUIET_ZONE_MODULES}
              />
            </button>
          </Tooltip.Primary>

          <div className={styles.qrInfo}>
            <p className={cn(styles.secondaryText, 'p2')}>
              {formatMessage({
                id: 'inviteColleague.qrCaption',
                defaultMessage:
                  'You can also show this QR code in person or share it in your presentations. Scanning it with a phone camera opens the same link.',
              })}
            </p>

            <button
              type="button"
              className={cn(styles.textButton, 'p2')}
              onClick={handleDownload}
            >
              {formatMessage({
                id: 'inviteColleague.downloadQrCode',
                defaultMessage: 'Download QR code',
              })}
            </button>
          </div>
        </div>
      )}

      <p className={cn(styles.footnote, 'p3')}>
        {formatMessage(
          {
            id: 'inviteColleague.footnote',
            defaultMessage:
              'Coins are credited to {clinicName} and appear in Billing info. They cover reports beyond your monthly plan limit.',
          },
          { clinicName: organization?.Name },
        )}
      </p>

      <QRCodeCanvas
        ref={qrCanvasRef}
        className={styles.exportCanvas}
        value={link}
        size={REFERRAL_QR_PNG.qrSize}
        level="M"
        marginSize={QR_QUIET_ZONE_MODULES}
        aria-hidden
      />

      <ReferralQrModal
        isOpen={isQrModalOpen}
        link={link}
        displayedLink={displayedLink}
        errorMessage={actionError}
        onClose={() => toggleQrModal(false)}
        onDownload={handleDownload}
      />
    </WidgetCard>
  );
};
